import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword, login } from '../lib/api';
import { setToken } from '../lib/auth';
import {
  NewPasswordFields,
  useAuthErrorText,
} from '../components/NewPasswordFields';
import { passwordProblem } from '../lib/operator-account';
import { Wordmark } from '../components/Brand';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { StatusBanner } from '../components/StatusBanner';
import { useLocale } from '../i18n/LocaleProvider';
import { HOME_PATH } from '../lib/navigation';

/**
 * The sign-in screen.
 *
 * Outside {@link Layout}, so it carries its own chrome: the landing page's gradient wash, the
 * wordmark, and one card. Same recipe as the portal's login — a Kanzlei and an operator arrive at
 * the same door, painted the same way.
 *
 * Two steps when the password was a one-time one (a new or reset account): the API issues no
 * token for it, so the second step asks for the operator's own password and only the change
 * signs them in. The one-time password stays in memory for that call and nowhere else.
 */
export function LoginPage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const errorText = useAuthErrorText();
  const draft = { username, current: password, next, confirm };

  /** Stores the session and goes to the ticket queue — support is the daily job here. */
  function signedIn(result: Awaited<ReturnType<typeof login>>) {
    if (!result.token || !result.expiresAt) {
      setError(t('login.failed'));
      return;
    }
    setToken(
      result.token,
      result.expiresAt,
      result.displayName,
      result.username,
    );
    navigate(HOME_PATH);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mustChange) {
        signedIn(await changePassword(username, password, next));
        return;
      }
      const result = await login(username, password);
      if (result.passwordChangeRequired) {
        setMustChange(true);
        return;
      }
      signedIn(result);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50/70 via-white to-white flex flex-col items-center justify-center px-4 py-12">
      {/* Top-right, where `Layout`'s header would carry it. Without it the one screen an
          operator meets before the header exists would be stuck in the detected language. */}
      <div className="fixed top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-sm">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2.5">
            <Wordmark />
          </div>
          <p className="eyebrow mt-4">{t('common.operations')}</p>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">
            {t(mustChange ? 'login.changeTitle' : 'login.title')}
          </h1>
          <p className="text-gray-600 text-sm mt-2">
            {t(mustChange ? 'login.changeLead' : 'login.lead')}
          </p>
        </div>

        <div className="card mt-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mustChange ? (
              <NewPasswordFields
                draft={draft}
                onNext={setNext}
                onConfirm={setConfirm}
              />
            ) : (
              <>
                <div>
                  <label className="field-label" htmlFor="username">
                    {t('login.username')}
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    className="field-input"
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="password">
                    {t('login.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="field-input"
                  />
                </div>
              </>
            )}

            {error && <StatusBanner tone="error">{error}</StatusBanner>}

            <button
              type="submit"
              disabled={
                loading || (mustChange && passwordProblem(draft) !== null)
              }
              className="btn-primary btn-sm w-full mt-2"
            >
              {loading
                ? t('login.submitting')
                : t(mustChange ? 'login.changeSubmit' : 'login.submit')}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          {t('login.footer')}
        </p>
      </div>
    </div>
  );
}
